import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney, convertMoneyToNumber } from '../../util/currency';
import { useCurrencyFormatter } from './useCurrencyFormatter';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const { formatAmount } = useCurrencyFormatter(isProvider ? 'provider' : 'customer');
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const lastTransition = transaction?.attributes?.lastTransition;
  const state = transaction?.attributes?.state;

  const isLateCancel = [
    'transition/customer-cancel-late',
    'transition/customer-cancel-late-from-interview',
  ].includes(lastTransition);

  const isCanceledState = state === 'canceled' || state === 'declined' || state === 'expired';
  // If transaction is canceled/declined/expired and NOT a late cancel by customer (where tutor gets paid), provider receives $0
  const isZeroPayoutForProvider = isProvider && isCanceledState && !isLateCancel;

  const totalPrice = isProvider
    ? (isZeroPayoutForProvider ? new Money(0, transaction?.attributes?.payoutTotal?.currency || 'USD') : transaction?.attributes?.payoutTotal)
    : transaction?.attributes?.payinTotal;
  const formattedTotalPrice = formatAmount(convertMoneyToNumber(totalPrice), totalPrice?.currency);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
